import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { api } from '../api';
import { HeadActions } from '../components/Layout';
import { useAuth } from '../auth';

export default function Devices() {
  const ctx = useOutletContext();
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [dss, setDss] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [pendingId, setPendingId] = useState(null);

  const canExclude = user?.role === 'admin' || user?.role === 'operator';
  const excludedCount = devices.filter((device) => device.includeInUptime === false).length;

  const load = async () => {
    setError('');
    try {
      const [list, status] = await Promise.all([api.devices(), api.dssStatus().catch(() => null)]);
      setDevices(list || []);
      setDss(status);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sync = async () => {
    setBusy(true);
    try {
      await api.syncDevices();
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleUptime = async (device) => {
    setPendingId(device.id);
    setError('');
    try {
      const updated = await api.updateDevice(device.id, {
        includeInUptime: !device.includeInUptime,
      });
      setDevices((list) => list.map((item) => (item.id === device.id ? { ...item, ...updated } : item)));
    } catch (err) {
      setError(err.message);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Devices</h1>
          <p>Camera inventory from Dahua DSS. Exclude a device to keep it out of uptime reports and live checks.</p>
        </div>
        <HeadActions {...ctx}>
          <button type="button" className="btn btn-gold" onClick={sync} disabled={busy}>
            {busy ? 'Syncing…' : 'Sync DSS'}
          </button>
        </HeadActions>
      </div>

      {error && <p className="error">{error}</p>}

      <section className="panel">
        <div className="panel-head">
          <strong>
            {devices.length} device{devices.length === 1 ? '' : 's'}
            {excludedCount ? ` · ${excludedCount} excluded` : ''}
          </strong>
          <span className={`badge ${dss?.connected ? 'badge-live' : 'badge-off'}`}>
            DSS {dss?.connected ? 'connected' : 'offline'}
          </span>
        </div>
        {!devices.length ? (
          <div className="empty">No devices. Press Sync DSS.</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>NAME</th>
                  <th>CODE</th>
                  <th>IP</th>
                  <th>STATUS</th>
                  <th>ORGANIZATION</th>
                  <th>UPTIME</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => {
                  const excluded = device.includeInUptime === false;
                  return (
                    <tr key={device.id} className={excluded ? 'excluded' : ''}>
                      <td>
                        <div className="device-uptime-cell">
                          {device.deviceName}
                          {!excluded && (
                            <Link className="btn" to={`/live?camera=${device.id}`}>
                              Live
                            </Link>
                          )}
                        </div>
                      </td>
                      <td className="muted">{device.deviceCode}</td>
                      <td>{device.deviceIp || '—'}</td>
                      <td>
                        <div className="status-cell">
                          <span className={`dot ${device.lastStatus || 'unchecked'}`} />
                          {(device.lastStatus || 'unchecked').toUpperCase()}
                        </div>
                      </td>
                      <td className="muted">{device.orgName || '—'}</td>
                      <td>
                        <div className="device-uptime-cell">
                          <span className={`badge ${excluded ? 'badge-off' : 'badge-live'}`}>
                            {excluded ? 'Excluded' : 'Included'}
                          </span>
                          {canExclude && (
                            <button
                              type="button"
                              className="btn"
                              disabled={pendingId === device.id}
                              onClick={() => toggleUptime(device)}
                            >
                              {pendingId === device.id ? 'Saving…' : excluded ? 'Include' : 'Exclude'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
